import { LoginError } from '@ninety/platform/models/auth/login-error.enum';

/** Shape common to a Cognito/Amplify v6 exception and a plain `Error`. */
export interface CognitoLikeError {
  name?: string;
  message?: string;
}

export const GENERIC_SIGN_IN_FAILURE = 'An error has occurred while logging in. Please refresh your page and try again';

/**
 * Cognito/Amplify v6 exception `name` -> member-facing sign-in message. Extend this, not the
 * function below, when the sign-in path starts producing a new exception.
 *
 * `UserNotFoundException` deliberately maps to the same message as `NotAuthorizedException`
 * (incorrect credentials): the login form must not let anyone tell an unregistered email apart
 * from a wrong password. `MFAMethodNotFoundException` and `ResourceNotFoundException` are folded
 * into the same "contact support" message as `InvalidUserPoolConfigurationException` — all three
 * are Cognito/pool setup problems a member can't resolve by retrying. `SignInTimeoutException` is
 * not a real Cognito exception; the login effects synthesize it when the 15s sign-in timeout
 * fires, so a stalled request resolves to a distinct, non-credential message through this map.
 */
const COGNITO_EXCEPTION_MESSAGES: Readonly<Record<string, string>> = {
  NotAuthorizedException: LoginError.INCORRECT,
  UserNotFoundException: LoginError.INCORRECT,
  PasswordResetRequiredException: LoginError.PASSWORD_RESET_REQUIRED,
  LimitExceededException: LoginError.RATE_LIMITED,
  UserNotConfirmedException: LoginError.UNCONFIRMED,
  InvalidUserPoolConfigurationException: LoginError.MISCONFIGURED,
  MFAMethodNotFoundException: LoginError.MISCONFIGURED,
  ResourceNotFoundException: LoginError.MISCONFIGURED,
  SignInTimeoutException: LoginError.CONNECTION_TIMEOUT,
};

/**
 * Pure mapping from a password sign-in failure to the message a member sees. The exception
 * `name` (not the legacy `code`, which Amplify v6 / AWS SDK v3 never set) picks the base
 * message; a message-text match against the `LoginError` vocabulary can override it, because
 * some failures arrive as messages from the Ninety API rather than as Cognito exceptions.
 */
export function mapCognitoErrorToMessage(error: unknown): string {
  const err = error as CognitoLikeError;
  let loginError = (err?.name && COGNITO_EXCEPTION_MESSAGES[err.name]) || GENERIC_SIGN_IN_FAILURE;

  if (err?.message) {
    const lastIdx = err.message.lastIndexOf('Error: ');
    const normalizedMessage = lastIdx !== -1 ? err.message.substring(lastIdx + 7) : err.message;
    if (
      normalizedMessage === LoginError.INCORRECT ||
      normalizedMessage === LoginError.PASSWORD_ATTEMPTS ||
      normalizedMessage === LoginError.NO_PERSON ||
      normalizedMessage === LoginError.NO_AVAILABLE_USERS_BASED_ON_DOMAIN
    ) {
      loginError = normalizedMessage;
    }
  }

  return loginError;
}
